import { useLayoutEffect } from "react";
import { ScrollView } from "react-native";
import { PressableScale } from "pressto";
import { useRouter, useLocalSearchParams, useNavigation } from "expo-router";
import { View, Text, ScreenContainer, Banner } from "@src/components";
import { useHeaderBackButton } from "@src/hooks/useHeaderBackButton";
import { Button } from "@src/components/Button";
import { format } from "date-fns";
import { useTenantId } from "@src/features/tenants";
import { useUnistyles } from "react-native-unistyles";
import { useExceptionSuggestions } from "@src/services/api/hooks";
import { formatSuggestionDate } from "@src/features/alarms/utils/exception-format";
import type { AlarmExceptionSuggestion } from "@src/services/api/types";
import { DiagonalStripeSquare } from "@src/features/schedules/components/DiagonalStripeSquare";
import { AlarmStateBadge } from "@src/features/alarms/components/AlarmStateBadge";
import { AlarmState } from "@src/features/clips";
import { Icon } from "@src/components/Icon";
import { formatTime12 } from "@src/utils/format-date";
import { capitalize } from "@src/utils/capitalize";
import { FeatureGate } from "@src/services/feature-flags/FeatureGate";
import { FEATURE_FLAGS } from "@src/services/feature-flags/feature-flags";

export default function CreateExceptionScreen() {
  const router = useRouter();
  const navigation = useNavigation();
  const { headerLeft } = useHeaderBackButton();
  const { siteId, cameraKey } = useLocalSearchParams<{
    siteId: string;
    cameraKey: string;
  }>();
  const tenantId = useTenantId();
  const { theme } = useUnistyles();
  const exceptionSuggestionsQuery = useExceptionSuggestions(siteId, cameraKey);
  const suggestedExceptions = exceptionSuggestionsQuery.data ?? [];

  const toDate = (date: AlarmExceptionSuggestion["date"]) => new Date(date.year, date.month - 1, date.day);

  const handleSelectSuggestion = (suggestion: AlarmExceptionSuggestion) => {
    const { reason, action, isAllDay } = suggestion;
    const date = toDate(suggestion.date);
    const startHour = isAllDay ? 0 : date.getHours();
    const startMinute = isAllDay ? 0 : date.getMinutes();
    const endHour = isAllDay ? 24 : date.getHours();
    const endMinute = isAllDay ? 0 : date.getMinutes();
    const formattedDate = format(date, "yyyy-MM-dd");

    const initialData: Record<string, string> = {
      reason,
      action,
      startDate: formattedDate,
      endDate: formattedDate,
      startHour: String(startHour),
      startMinute: String(startMinute),
      endHour: String(endHour),
      endMinute: String(endMinute),
      endIs24Hour: String(isAllDay),
    };

    // Navigate to edit screen with "new" ID and initial data
    const paramsString = new URLSearchParams(initialData).toString();
    router.push(`/tenants/${tenantId}/sites/${siteId}/cameras/${cameraKey}/alarms/exceptions/new?${paramsString}`);
  };

  const handleAddTimeRange = () => {
    // Navigate to edit screen with "new" ID
    router.push(`/tenants/${tenantId}/sites/${siteId}/cameras/${cameraKey}/alarms/exceptions/new`);
  };

  // Configure navigation header
  useLayoutEffect(() => {
    navigation.setOptions({
      title: "Add Exception",
      headerBackButtonDisplayMode: "minimal",
      headerLeft,
    });
  }, [navigation, headerLeft]);

  return (
    <ScreenContainer edges={[]}>
      <ScrollView style={{ flex: 1 }} contentContainerStyle={{ paddingBottom: 40 }}>
        {/* Info Card */}
        <View px="5xl" pt="4xl">
          <Banner
            variant="info"
            title="What is an exception?"
            message={`Exceptions temporarily override the schedule for specific dates or time ranges, like holidays or maintenance.\nEg: Arm all day on New Year's Day.`}
          />
        </View>
        <FeatureGate flag={FEATURE_FLAGS.ALARM_EXCEPTION_SUGGESTIONS}>
          {/* Suggested Section */}
          <View px="5xl" py="4xl">
            <Text textStyle="xs/medium" color="textMuted" mb="lg">
              Suggested
            </Text>
            <View gap="lg">
              {suggestedExceptions.map((suggestion) => {
                const { date, time } = formatSuggestionDate(suggestion);
                return (
                  <PressableScale key={suggestion.id} onPress={() => handleSelectSuggestion(suggestion)}>
                    <View
                      style={{ backgroundColor: theme.colors.surface }}
                      flexDirection="row"
                      justifyContent="space-between"
                      borderWidth={1}
                      borderColor="border"
                      p="4xl"
                      rounded={8}
                    >
                      <View>
                        <View flexDirection="row" alignItems="center" gap="md" mb="2xl">
                          <View style={{ overflow: "hidden" }}>
                            <DiagonalStripeSquare state={suggestion.action as "armed" | "disarmed"} size={20} />
                          </View>
                          <Text textStyle="sm/medium" style={{ color: theme.colors.text }}>
                            {suggestion.reason}
                          </Text>
                          <AlarmStateBadge state={suggestion.action} />
                        </View>
                        <View flexDirection="row" alignItems="center" gap="xs">
                          <Icon name="calendarOutline" color={theme.colors.textMuted} size={16} />
                          <Text color="textMuted" textStyle="xs/regular">
                            {date}
                          </Text>
                          {time && (
                            <>
                              <Text color="textMuted" textStyle="xs/regular">
                                •
                              </Text>
                              <Icon name="clockOutline" color={theme.colors.textMuted} size={16} />
                              <Text color="textMuted" textStyle="xs/regular">
                                {capitalize(time)}
                              </Text>
                            </>
                          )}
                        </View>
                        {"otherCamerasCount" in suggestion &&
                          typeof suggestion.otherCamerasCount === "number" &&
                          suggestion.otherCamerasCount > 0 && (
                            <Text color="navy" textStyle="xs/regular" mt="xs">
                              {suggestion.otherCamerasCount} other cameras
                            </Text>
                          )}
                      </View>
                      <Icon name="chevronRight" color={theme.colors.textMuted} />
                    </View>
                  </PressableScale>
                );
              })}
            </View>
          </View>
        </FeatureGate>

        {/* New Exception Button */}
        <View px="5xl" py="2xl">
          <Button label="Create new" variant="primary" variantColor="blue" size="lg" onPress={handleAddTimeRange} />
        </View>
      </ScrollView>
    </ScreenContainer>
  );
}
